/**
 * @file hooks/useDerivatives
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 * @author Phil Tietjen [phil.tietjen@cbc.ca]
 */
import { useMemo } from "react";
import {
    IMAGE_INDICATOR,
    DERIVATIVE_PREFIX,
    DERIVATIVE_SUFFIX,
    DERIVATIVE_SIZE_PLACEHOLDER,
    DEFAULT_DERIVATIVE_WIDTHS,
} from "./constants";

/**
 *
 * @description Takes an orginal imageUrl generated by polopoly and creates arrays of srcSet and sizes values based on sizes and aspect ratios given.
 * @param {string} imageUrl
 * @param {number[]} [derivativeWidths]
 * @param {string} [aspectRatio="16x9"]
 * @returns {array} an array containing srcSet,sizes properties.
 */
function useImageDerivatives(
    imageUrl,
    derivativeWidths = DEFAULT_DERIVATIVE_WIDTHS,
    aspectRatio = "16x9",
    useOriginalImage = false
) {
    return useMemo(() => {
        let srcSet = [];
        let sizes = [];

        if (!imageUrl || (/\.gif/gi.test(imageUrl) && useOriginalImage)) {
            return [srcSet, sizes];
        }

        if (
            imageUrl &&
            imageUrl.includes(IMAGE_INDICATOR) &&
            Array.isArray(derivativeWidths) &&
            derivativeWidths.length > 0
        ) {
            let derivativeUrlPlaceholder = "";
            const imageExt = imageUrl.slice(imageUrl.search(/[^.]+$/gi));

            // Account for imageUrl given being a derivative
            if (
                imageUrl.includes(DERIVATIVE_PREFIX) &&
                imageUrl.includes(DERIVATIVE_SUFFIX)
            ) {
                derivativeUrlPlaceholder = imageUrl.replace(
                    RegExp(
                        `(?:${DERIVATIVE_PREFIX}.${imageExt}${DERIVATIVE_SUFFIX})([A-z0-9]+_[0-9]+)`,
                        "i"
                    ),
                    (match, p1) => {
                        return match.replace(
                            p1,
                            `${aspectRatio}_${DERIVATIVE_SIZE_PLACEHOLDER}`
                        );
                    }
                );
            } else {
                derivativeUrlPlaceholder = imageUrl.replace(
                    IMAGE_INDICATOR,
                    `${DERIVATIVE_PREFIX}.${imageExt}${DERIVATIVE_SUFFIX}${aspectRatio}_${DERIVATIVE_SIZE_PLACEHOLDER}/`
                );
            }

            // construct srcset for Polopoly image
            if (imageUrl.indexOf("?") < 0) {
                srcSet = derivativeWidths.map(
                    (width) =>
                        `${derivativeUrlPlaceholder.replace(
                            DERIVATIVE_SIZE_PLACEHOLDER,
                            `${width}`
                        )} ${width}w`
                );
                // construct srcset for AIS image with IMQuery
            } else {
                srcSet = derivativeWidths.map(
                    (width) => `${imageUrl}Resize%3D${width} ${width}w`
                );
            }

            sizes = derivativeWidths.map(
                (width) => `(max-width: ${width}px) ${width}px`
            );
        }
        return [srcSet, sizes];
    }, [imageUrl, derivativeWidths, aspectRatio]);
}
export default useImageDerivatives;
